import { getRulerOrderedFrameIds } from "@/lib/frame-ticks";
import { usePresentationStore } from "@/stores/presentation-store";
import { useSelectionStore } from "@/stores/selection-store";
import { useFramesStore } from "@/stores/frames-store";
import { selectOrderedFrames } from "@/stores/frames-selectors";
import {
  addFrameToFocusPresentation,
  enterPresentationMode,
  exitPresentationMode,
} from "@/lib/presentation-mode";
import type { FrameData } from "@/lib/frame-data";
import { focusPresentedFramesInDesktopMode } from "@/lib/focus/focus-desktop-mode";
import type { FocusCardOrigin, FocusPlacement } from "@/lib/focus/focus-types";
import { isTauri } from "@/lib/is-tauri";

/** Start frame for focus mode: the current selection, else the first note
 *  or Doc, else the first taskboard, else the first frame. */
export function pickFocusStartId(frames: readonly FrameData[]): string | null {
  const selectedFrameId = useSelectionStore.getState().selectedFrameId;
  const selectedId =
    selectedFrameId && frames.some((frame) => frame.frameId === selectedFrameId)
      ? selectedFrameId
      : null;
  return (
    selectedId ??
    frames.find(
      (frame) => frame.frameType === "note" || frame.frameType === "doc",
    )?.frameId ??
    frames.find((frame) => frame.frameType === "taskboard")?.frameId ??
    frames[0]?.frameId ??
    null
  );
}

export function enterFocusPresentationMode(
  startFrameId?: string,
  frameIds?: string[],
) {
  const framesState = useFramesStore.getState();
  const { framesById } = framesState;
  const frames = selectOrderedFrames(framesState);
  const start = startFrameId ?? pickFocusStartId(frames);
  const ids =
    frameIds && frameIds.length > 0
      ? frameIds.filter((id) => !!framesById[id])
      : start
        ? [start]
        : getRulerOrderedFrameIds(frames);
  if (start && !ids.includes(start)) ids.unshift(start);
  if (ids.length === 0) return;
  const familyStart = start ?? ids[0]!;
  const conversationFamily =
    framesById[familyStart]?.frameType === "conversation";
  const familyIds = ids.filter((id) => {
    const frame = framesById[id];
    return (
      !!frame && (frame.frameType === "conversation") === conversationFamily
    );
  });
  const explicitFocusRequest =
    !!startFrameId || (frameIds !== undefined && frameIds.length > 0);
  if (
    usePresentationStore.getState().presentation?.variant === "focus" &&
    explicitFocusRequest
  ) {
    familyIds.forEach((id) => addFrameToFocusPresentation(id));
    return;
  }
  enterPresentationMode("focus", familyIds, familyStart);
}

/** Replace the current presentation with exactly the requested frames.
 * Desktop focus actions use this path so old focus windows never leak into a
 * new Compact overlay selection. */
export function replaceFocusPresentationMode(
  startFrameId?: string,
  frameIds?: string[],
  {
    placement,
    fromCard,
    displayId,
  }: {
    placement?: FocusPlacement;
    fromCard?: FocusCardOrigin;
    displayId?: number;
  } = {},
) {
  const state = useSelectionStore.getState();
  const requestedIds =
    frameIds && frameIds.length > 0
      ? frameIds
      : state.multiSelectedFrameIds.length > 0
        ? state.multiSelectedFrameIds
        : startFrameId
          ? [startFrameId]
          : state.selectedFrameId
            ? [state.selectedFrameId]
            : [
                pickFocusStartId(
                  selectOrderedFrames(useFramesStore.getState()),
                ),
              ].filter((id): id is string => id !== null);
  const { framesById } = useFramesStore.getState();
  const ids = [...new Set(requestedIds)].filter((id) => !!framesById[id]);
  const start =
    (startFrameId && ids.includes(startFrameId) ? startFrameId : ids[0]) ??
    null;
  if (!start || ids.length === 0) return;
  enterPresentationMode("focus", ids, start, {
    allowDesktopMirror: true,
    placement,
    fromCard,
    displayId,
  });
}

/**
 * An explicit Focus (card menu, frame menu, multi-select, a preview's Focus
 * button): focus mode, where on desktop the Workspace steps aside and the
 * frames float over the desktop until Back. Frames an open focus already
 * shows move there as they are, unless it runs on another display than
 * `displayId` asks for; the web shows them above the wall.
 */
export function enterExplicitFocus(
  frameIds: readonly string[],
  { displayId }: { displayId?: number } = {},
): void {
  if (frameIds.length === 0) return;
  const presentation = usePresentationStore.getState().presentation;
  if (
    presentation?.variant === "focus" &&
    (displayId === undefined || presentation.displayId === displayId) &&
    frameIds.every((id) => presentation.frameIds.includes(id))
  ) {
    focusPresentedFramesInDesktopMode(presentation.frameIds, {
      allowMirror: true,
      preview: false,
    });
    return;
  }
  replaceFocusPresentationMode(frameIds[0], [...frameIds], {
    placement: "focus",
    displayId,
  });
}

/** The focus-mode toggle (shortcut and /focus): an open focus ends;
 *  otherwise the selection is focused, replacing any presentation on desktop. */
export function toggleFocusPresentationMode(): void {
  if (usePresentationStore.getState().presentation?.variant === "focus") {
    exitPresentationMode();
    return;
  }
  if (isTauri()) {
    replaceFocusPresentationMode();
    return;
  }
  enterFocusPresentationMode();
}
